import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Menu, X } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

const Header = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      {/* Top Bar - Contact Info */}
      <div className={`bg-blue-500 text-slate-900 transition-all duration-300 ${
        isScrolled ? 'h-0 overflow-hidden opacity-0' : 'h-auto py-2'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center md:justify-between items-center text-sm font-medium gap-4">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <a href={`tel:${companyInfo.phone}`} className="hover:text-white transition-colors">
                {companyInfo.phone}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>{companyInfo.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{companyInfo.address}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="relative">
              {/* Animated lights on sides - only on desktop */}
              <div className="hidden md:block absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-400 rounded-full blur-sm animate-pulse" style={{animationDuration: '1.5s'}} />
              <div className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-400 rounded-full blur-sm animate-pulse" style={{animationDuration: '1.5s', animationDelay: '0.75s'}} />
              
              {/* Glow effect - only on desktop */}
              <div className="hidden md:block absolute inset-0 bg-blue-500/20 rounded-lg blur-md animate-pulse" style={{animationDuration: '2s'}} />
              
              <img
                src={companyInfo.logoUrl}
                alt={companyInfo.name}
                className="relative h-14 md:h-16 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden h-14 md:h-16 px-4 bg-slate-800 rounded-lg items-center justify-center">
                <span className="text-blue-500 font-bold text-xl">ZHR</span>
              </div>
            </div>
            <div className="hidden md:block">
              <h1 className="text-white font-bold text-lg leading-tight">
                {companyInfo.name}
              </h1>
              <p className="text-blue-400 text-xs">{companyInfo.tagline}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <button
              onClick={() => scrollToSection('inicio')}
              className="text-white hover:text-blue-400 transition-colors font-medium"
            >
              {t('nav.home')}
            </button>
            <button
              onClick={() => scrollToSection('servicios')}
              className="text-white hover:text-blue-400 transition-colors font-medium"
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('galeria')}
              className="text-white hover:text-blue-400 transition-colors font-medium"
            >
              {t('nav.gallery')}
            </button>
            <button
              onClick={() => scrollToSection('testimonios')}
              className="text-white hover:text-blue-400 transition-colors font-medium"
            >
              {t('nav.testimonials')}
            </button>
            <LanguageToggle />
            <button
              onClick={() => scrollToSection('contacto')}
              className="bg-blue-500 text-slate-900 px-6 py-2 rounded-lg font-bold hover:bg-blue-400 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/50"
            >
              {t('nav.contact')}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 py-4 bg-slate-800/95 backdrop-blur-md rounded-lg">
            <div className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection('inicio')}
                className="text-white hover:text-blue-400 transition-colors font-medium px-4 py-2 text-left"
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('servicios')}
                className="text-white hover:text-blue-400 transition-colors font-medium px-4 py-2 text-left"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => scrollToSection('galeria')}
                className="text-white hover:text-blue-400 transition-colors font-medium px-4 py-2 text-left"
              >
                {t('nav.gallery')}
              </button>
              <button
                onClick={() => scrollToSection('testimonios')}
                className="text-white hover:text-blue-400 transition-colors font-medium px-4 py-2 text-left"
              >
                {t('nav.testimonials')}
              </button>
              <div className="px-4">
                <LanguageToggle />
              </div>
              <button
                onClick={() => scrollToSection('contacto')}
                className="bg-blue-500 text-slate-900 px-4 py-2 rounded-lg font-bold hover:bg-blue-400 transition-colors mx-4"
              >
                {t('nav.contact')}
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
